import React from "react";
import PropTypes from "prop-types";
import { CSSAbsolute, CSSRelative, CSSFlex } from "GlobalComponent/CSSCore";
import getThemeClasses from "@oxygen/theme";
import { ChevronRight } from "@oxygen/icons";
import styles from "./scss/BannerDropDown.module.scss";

const propTypes = {
  /** Active Index of element */
  activeIndex: PropTypes.number,
  /** if `true`, dropdown is visible. */
  visible: PropTypes.bool,
  /** Callback fired on dropdown is opened with activeIndex */
  onDropDownOpen: PropTypes.func,
  /** Callback fired on dropdown is closed with activeIndex */
  onDropDownClose: PropTypes.func,
  /** Callback fired on dropdown element is selected with event and activeIndex */
  onSelect: PropTypes.func,
};

const defaultProps = {
  visible: false,
  activeIndex: 0,
};

const dropDownContainerClasses = getThemeClasses(`
  z-index-1;
`);

function getDropDownElementsContainerClasses(isActive) {
  return getThemeClasses(`
    ${isActive ? "display-block" : "display-none"};
    border-top-width-1;
    border-top-style-solid;
    border-color-lightGray;
  `);
}

const dropDownElementClasses = getThemeClasses(`
  position-relative;
  align-items-center;
  justify-content-space-between;
  p-3;
  font-size-7.5;
  bg-gainsBoroGray;
  color-charcoal;
  text-align-left;
  line-height-standard;
  cursor-pointer;
`);

function getDropDownFirstElementClasses(isActive) {
  return getThemeClasses(`
    z-index-1;
    border-top-left-radius-3;
    ${isActive ? styles.rotateArrow : "border-bottom-left-radius-3"};
  `);
}

function getDropDownLastElementClasses(isLast) {
  if (isLast) {
    return getThemeClasses(`
      border-bottom-left-radius-3;
      border-bottom-right-radius-3;
    `);
  }

  return "";
}

class BannerDropDown extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: props.activeIndex,
      visible: props.visible,
    };
  }

  componentDidMount() {
    window.addEventListener("click", this.handleDropDownVisibility);
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.handleDropDownVisibility);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.visible !== this.props.visible) {
      this.setState({ visible: this.props.visible });
    }
    if (prevProps.activeIndex !== this.props.activeIndex) {
      this.setState({ activeIndex: this.props.activeIndex });
    }
  }

  handleDropDownVisibility = () => {
    if (this.state.visible) {
      this.toggleDropdown();
    }
  };

  setList = () => {
    let list = [];
    const { children } = this.props;

    React.Children.map(children, (element, index) => {
      if (!React.isValidElement(element)) return;
      if (index === this.state.activeIndex) {
        list.splice(0, 0, <div data-index={index}>{element}</div>);
      } else {
        list.push(<div data-index={index}>{element}</div>);
      }
    });
    return list;
  };

  toggleDropdown = event => {
    const { onDropDownOpen, onDropDownClose } = this.props;
    event && event.stopPropagation();
    this.setState({ visible: !this.state.visible }, () => {
      if (!this.state.visible) {
        if (onDropDownOpen) {
          onDropDownOpen(this.state.activeIndex);
        }
      } else {
        if (onDropDownClose) {
          onDropDownClose(this.state.activeIndex);
        }
      }
    });
  };

  setActiveIndex = event => {
    const activeIndex = Number(
      event.currentTarget.children[0].getAttribute("data-index")
    );
    this.setState(
      {
        activeIndex: activeIndex,
        visible: false,
      },
      () => {
        if (this.props.onSelect) {
          this.props.onSelect(event, activeIndex);
        }
      }
    );
  };

  renderChildren = () => {
    const list = this.setList();
    const otherList = list.slice(1);
    const { visible } = this.state;

    const dropdownClasses = `${dropDownElementClasses} ${
      styles.dropDownElement
    } ${getDropDownFirstElementClasses(visible)} ${
      styles.dropDownFirstElement
    }`;

    return (
      <>
        <CSSFlex
          onClick={this.toggleDropdown}
          className={dropdownClasses}
          data-testing-id="new-dropdown"
        >
          {list[0]}
          <ChevronRight
            size="xsm"
            className={styles.arrowIcon}
            lazyLoad={false}
          />
        </CSSFlex>
        <CSSAbsolute className={getDropDownElementsContainerClasses(visible)}>
          {otherList.length &&
            otherList.map((element, index) => {
              const isLastElement = index === otherList.length - 1;

              return (
                <CSSFlex
                  onClick={this.setActiveIndex}
                  key={index}
                  className={`${dropDownElementClasses} ${
                    styles.dropDownElement
                  } ${getDropDownLastElementClasses(isLastElement)}`}
                  data-testing-id="used-dropdown"
                >
                  {element}
                </CSSFlex>
              );
            })}
        </CSSAbsolute>
      </>
    );
  };

  render() {
    return (
      <CSSRelative
        className={`${dropDownContainerClasses} ${styles.dropDownContainer}`}
      >
        {this.renderChildren()}
      </CSSRelative>
    );
  }
}

BannerDropDown.propTypes = propTypes;
BannerDropDown.defaultProps = defaultProps;

export default BannerDropDown;
